import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../hooks/useContriesData";
import useData from "../../hooks/useData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../config/config";
import { formatNumberWithDot } from "../../hooks/utils";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer} from "deck.gl";
import MapControls from "../common/map/MapControls";
import MapToolTip from "../common/mapTooltip/MapToolTip";
import styles from "./flow.module.scss";
import FlowDetail from "../common/popup/FlowDetail";

const MigrationFlow = () => {
  let {wealthMigrationData} = useData();
  const [hoveredCity, setHoveredCity] = useState(null);
  const [allCitiesData, setAllCitiesData] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [radioboxSelected, setRadioboxSelected] = useState('All');
  const [detailCard, setDetailCard] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  const [topNoteText, setTopNoteText] = useState('inflows and outflows');
  let mapRef = useRef(null);
  let tableRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);
  const countriesData = useCountriesData();
  const popUpRef = useRef(null)

  useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );


  useEffect(() => {

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO3)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO3)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }
    
    if(wealthMigrationData && countriesData){
      const inflowData = wealthMigrationData.inflow.map((item) => {
          return {
            ...item,
            category: "inflow"
          };
      });

      const outflowData = wealthMigrationData.outflow.map((item) => {
          return {
            ...item,
            category: "outflow"
          };
      });

      const allflowData = inflowData.concat(outflowData);
      // setAllCitiesData(allflowData);

      const includeCountries = allflowData.map((d) => {
        return d.ISO3;
      });
      const includeInflowCountires = inflowData.map((d) => {
        return d.ISO3;
      });
      const includeOutflowCountires = outflowData.map((d) => {
        return d.ISO3;
      });

      let features = countriesData.features;   

      if(radioboxSelected === 'All'){
        setAllCitiesData(allflowData.sort((a, b) => b.netHNWIs2022Provisional - a.netHNWIs2022Provisional));
        showVisibleCountries(includeCountries, features);
        hideNotVisibleCountries(includeCountries, features);
        setTopNoteText('inflows and outflows');
      }else if(radioboxSelected === 'Inflow'){
        setAllCitiesData(inflowData.sort((a, b) => b.netHNWIs2022Provisional - a.netHNWIs2022Provisional));
        showVisibleCountries(includeInflowCountires, features);
        hideNotVisibleCountries(includeInflowCountires, features);
        setTopNoteText('inflows');
      }else if(radioboxSelected === 'Outflow'){
        setAllCitiesData(outflowData.sort((a, b) => a.netHNWIs2022Provisional - b.netHNWIs2022Provisional));
        showVisibleCountries(includeOutflowCountires, features);
        hideNotVisibleCountries(includeOutflowCountires, features);
        setTopNoteText('outflows');
      }
    }
  }, [wealthMigrationData, countriesData, radioboxSelected]);

    const getDataViaMap = (mapData, sourceData) =>{
      const targetData = sourceData.filter(item => item.ISO3 === mapData.object.properties.ISO3);
      return targetData[0];
    }

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };

  const handleRadioboxChange = (event) =>{
    setRadioboxSelected(event.target.value);
  }

  const handleHover = (data) => {
    setHoveredCity(data !== null ? data.country : null);
  };

  const handleMapHover = (data) => {
    setHoveredCity(data.object ? getDataViaMap(data, allCitiesData).country : null);
  };


  const handleClick = (data, fromMap=false) =>{
    // console.log(data);
    setDetailCard(fromMap? getDataViaMap(data, allCitiesData) : data);
    setShowCard(true);
    popUpRef.current.scrollIntoView({ behavior: "smooth" });
  }

  const cardClose = () =>{
    setShowCard(false);
  }

  const getFillColor = (d) =>{
    const targetData = allCitiesData.filter(item => item.ISO3 === d.properties.ISO3);
    return targetData[0].category === 'inflow' ? [125, 176, 218] : [216, 111, 111];
  }



  return (
    <div className="appContainer">
      <div className="container-fluid">
        <div className="row flex-md-row-reverse">
          <div className="col-md-8 col-lg-9">
            <p>The high-net-worth-individual inflows and outflows data represents the top 10 countries globally in terms of net inflows and outflows of millionaires projected for 2022 (namely, the difference between the number of high-net-worth individuals who relocate to and the number who emigrate from a country). Net inflows indicates more high-net-worth individuals relocating to a country than leaving, while net outflows indicates more millionaires emigrating than arriving. Nine of the top 10 countries for high-net-worth-individual inflows in 2022 host formal <a className="text-underline" href="https://www.henleyglobal.com/countries" target="_blank" rel="noreferrer">investment migration</a> programs and actively encourage foreign direct investment in return for <a className="text-underline" href="https://www.henleyglobal.com/residence-investment" target="_blank" rel="noreferrer">residence</a> or <a className="text-underline" href="https://www.henleyglobal.com/citizenship-investment" target="_blank" rel="noreferrer">citizenship</a> rights.</p>
          </div>
          <div className="col-md-4 col-lg-3">
            <h3>Click on the map or the list below to see detailed country information</h3>
            <div className="custom-radiobox-group mb-4">
              <label className="custom-radiobox">
                <input type="radio" value="All" name="flow" checked={radioboxSelected === 'All'} onChange={handleRadioboxChange} /> 
                <span className="label-text">All</span>
              </label>
              <label className="custom-radiobox">
                 <input type="radio" value="Inflow" name="flow" checked={radioboxSelected === 'Inflow'} onChange={handleRadioboxChange} /> 
                 <span className="label-text"><span className="circle circle-inflow"></span> Inflows</span>
              </label>
              <label className="custom-radiobox">
                <input type="radio" value="Outflow" name="flow" checked={radioboxSelected === 'Outflow'} onChange={handleRadioboxChange} />
                <span className="label-text"><span className="circle circle-outflow"></span> Outflows</span>
              </label>
            </div>
          </div>
        </div>

        <div className="row flex-md-row-reverse">
          <div className="col-md-8 col-lg-9">
            <div className={styles.mapColumn}>
              <div className={styles.mapWrapper} ref={mapRef}>
                <DeckGL
                  viewState={viewport}
                  controller={{scrollZoom:false, doubleClickZoom: false}}
                  onViewStateChange={handleViewStateChange}
                >
                  <GeoJsonLayer
                    id="map-unselected-countries"
                    data={unselectedCountries ? unselectedCountries : null}
                    getLineColor={[255, 255, 255]}
                    getFillColor={[233, 233, 233]}
                    lineWidthUnits={"pixels"}
                    stroked={true}
                  />
                  <GeoJsonLayer
                    id="map-selected-countries"
                    data={selectedCountries ? selectedCountries : null}
                    getLineColor={[255, 255, 255]}
                    // getFillColor={[193, 197, 200]}
                    getFillColor = { d => getFillColor(d)}
                    lineWidthUnits={"pixels"}
                    stroked={true}
                    pickable={true}
                    onClick={(info) => handleClick(info, true)}
                    onHover={(info) => handleMapHover(info)}
                  />
                </DeckGL>
                {/* <svg
                  className={styles.cityMarkers}
                  height={"100%"}
                  width={"100%"}
                  viewBox={`0 0 ${width} ${height}`}
                >
                  {allCitiesData &&
                    allCitiesData.map((d) => {
                      return (
                        <circle
                          className={styles.circle}
                          key={d.country + "-marker"}
                          cx={getProjectionCoordinates([d.longitude, d.latitude])[0]}
                          cy={getProjectionCoordinates([d.longitude, d.latitude])[1]}
                          r={hoveredCity === d.country ? 10 : 5}
                          fill={"#405363"}
                          stroke={"#fff"}
                          onTouchStart={() => {
                            handleHover(d.country, true);
                          }}
                          onMouseEnter={() => {
                            handleHover(d.country, true);
                          }}
                          onMouseOut={() => {
                            handleHover(null);
                          }}
                          onClick={() => handleClick(d)}
                        >
                          <title>{d.country}</title>
                        </circle>
                      );
                    })}
                </svg> */}
                {allCitiesData &&
                  allCitiesData.map((d) => {
                    return (
                      <React.Fragment key={d.country + "-tooltip"}>
                        {hoveredCity === d.country && (
                          <MapToolTip 
                            name={d.country} 
                            top={getProjectionCoordinates([d.longitude,d.latitude,])[1] - 10}
                            left={getProjectionCoordinates([d.longitude,d.latitude,])[0] - 10}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  <MapControls
                    zoomIncrement={() => zoomIncrement(0.5)}
                    zoomDecrement={() => zoomIncrement(-0.5)}
                    resetZoom={() => resetZoom()}
                  /> 
              </div>
              <div ref={popUpRef} className={styles.positionToScroll}></div>
              {showCard ? <FlowDetail item={detailCard} onClose={cardClose} /> : null}
            </div>
            
          </div>
          <div className="col-md-4 col-lg-3">
            <p className="mt-4 mt-md-0 mb-3">Projected net {topNoteText} of HNWIs 2022</p>
            <div className={styles.tableContainer} ref={tableRef}>
              <table className={styles.mapTable}>
                <tbody>
                  {allCitiesData &&
                    allCitiesData.map((d) => {
                      const circleMark = d.category === "inflow" ? (<span className="circle circle-inflow"></span>) : (<span className="circle circle-outflow"></span>);
                      return (
                        <tr
                          id={`table-row-${d.country}`}
                          key={d.country}
                          style={{
                            backgroundColor:
                              hoveredCity === d.country ? "#fff" : null,
                          }}
                          onMouseOver={() => {
                            handleHover(d);
                          }}
                          onMouseOut={() => {
                            handleHover(null);
                          }}
                          onClick={() => handleClick(d)}
                        >
                          
                          <td className={styles.countryColumn}>
                            <div className={styles.countryName}>{circleMark} <span className={styles.countryNameText}>{d.country}</span></div>
                          </td>
                          <td className={styles.valueColumn}>
                            {/* {d.totalScore.toFixed(0)} */}
                            {formatNumberWithDot(d.netHNWIs2022Provisional)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              <p className="mt-3 footer-note">These are projected figures for the full year 2022. They are based on year-to-date HNWI movements. Numbers rounded to the nearest 100.</p>
            </div>
          </div>
        </div>
      </div>
    
    
     
    </div>
  );
}

export default MigrationFlow;